/**
 * Identidade compacta do problema. Uma informação, um lugar.
 */
import React from 'react';
import { ArrowLeft } from 'lucide-react';
import type { Issue } from '../../types/issue';
import {
  ISSUE_STATUS_LABELS, ISSUE_SEVERITY_LABELS, ISSUE_TYPE_LABELS, ISSUE_MODE_LABELS,
} from '../../types/issue';
import type { EnrichedProblemIssue } from '../../services/problemDashboardData';
import { getIssueSlaDays } from '../../utils/issueSla';

const SEV_COLOR: Record<string, string> = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-slate-500 text-white',
};

interface ProblemWorkspaceHeaderProps {
  issue: Issue;
  enriched: EnrichedProblemIssue;
  assigneeLabel: string;
  onBack: () => void;
}

export const ProblemWorkspaceHeader: React.FC<ProblemWorkspaceHeaderProps> = ({
  issue,
  enriched,
  assigneeLabel,
  onBack,
}) => {
  const settled = issue.status === 'closed';
  const windowDays = getIssueSlaDays(issue.severity);
  const overdue = !settled && enriched.flags.slaOverdue;
  const dueLabel = settled
    ? null
    : overdue
      ? `Prazo vencido · ${Math.abs(enriched.slaRemainingDays)} dias`
      : `Prazo · ${enriched.slaRemainingDays}d de ${windowDays}d`;

  return (
    <header className="shrink-0 border-b border-slate-200 py-1.5 dark:border-slate-800">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[12px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800"
        >
          <ArrowLeft size={14} />
          Lista
        </button>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${SEV_COLOR[issue.severity]}`}>
          {ISSUE_SEVERITY_LABELS[issue.severity]}
        </span>
        <span className="text-[12px] font-medium text-slate-700 dark:text-slate-200">
          {ISSUE_STATUS_LABELS[issue.status]}
        </span>
        <span className="text-[12px] text-slate-500">{ISSUE_TYPE_LABELS[issue.type]}</span>
        {issue.mode ? (
          <span className="text-[12px] text-slate-400">{ISSUE_MODE_LABELS[issue.mode]}</span>
        ) : null}
        {issue.code ? <span className="font-mono text-[11px] text-slate-400">{issue.code}</span> : null}
      </div>
      <h1 className="mt-0.5 truncate text-[16px] font-semibold leading-tight text-slate-950 dark:text-slate-50">
        {issue.title}
      </h1>
      <p className="mt-0.5 flex flex-wrap gap-x-2 text-[12px] text-slate-600 dark:text-slate-300">
        <span>{assigneeLabel}</span>
        {dueLabel ? <span className="text-slate-300">·</span> : null}
        {dueLabel ? (
          <span className={overdue ? 'font-medium text-red-700 dark:text-red-400' : ''}>{dueLabel}</span>
        ) : null}
        <span className="text-slate-300">·</span>
        <span className="text-slate-500">Aberto há {enriched.daysOpen} dias</span>
      </p>
    </header>
  );
};

export default ProblemWorkspaceHeader;
